// The board. Squares are drawn once. Highlights redraw on every change.
// Pieces are reconciled by id so a move slides instead of blinking.
// Everything about "what can I click" comes from the cue.

import {
  BOARD_SIZE,
  CARRY_PIVOT_PX,
  markDitherId,
  PIECE_LEAVE_MS,
  PITCH_ARROW,
  REDUCED_MOTION_QUERY,
  SPARK_TRAVEL_MS,
  SQUARE_NAME_INSET_PX,
  SQUARE_PX,
} from '../constants';
import { isSquareAttacked, movesForPiece } from '../engine/moves';
import { roleSignature } from '../engine/roles';
import { ALL_SQUARES, fileOf, movePath, rankOf, squareName } from '../engine/squares';
import { traitSignature } from '../engine/traits';
import type { Board, Move, Owner, Piece } from '../engine/types';
import type { Cue } from '../game/cue';
import {
  aimsAtRegion,
  legalMovesFrom,
  positionOf,
  regionOfPick,
  removedByPick,
} from '../game/flow';
import type { HistoryStep } from '../game/history';
import type { GameState } from '../game/state';
import { svg } from '../lib/svg';
import type { CarryPose } from '../motion/carry';
import { playSound } from '../sound/player';
import { type Carry, captureDelayMs, carryStore, landDelayMs, poseOfCarry } from '../state/carry';
import { type Beat, beatStore } from '../state/director';
import { reviewedStep } from '../state/history';
import { layoutStore } from '../state/layout';
import { localSideStore } from '../state/localSide';
import { settingsStore } from '../state/settings';
import { type StagePhase, stageStore } from '../state/stage';
import { PITCH_KINDS, type Pitches, type PitchKind, suggestionStore } from '../state/suggestion';
import { clickSquare, hoverSquare, type UiState, uiStore } from '../state/ui';
import { currentCue, subscribeView, viewedGame } from '../state/view';
import { waitingPiecesStore } from '../state/waitingPieces';
import { type BadgeSet, createBadgeSet } from './Badges';
import { boardOrigin } from './boardOrigin';
import { createCloud } from './Cloud';
import { createPieceGlyph, GLYPH_BOX, glyphBounds } from './PieceGlyph';
import { choiceMark, crossMark, focusMark, regionMark, targetMark, traceMark } from './SquareMarks';
import { createTerrainLayer } from './Terrain';

const BOARD_PX = BOARD_SIZE * SQUARE_PX;
const DOT_RADIUS = 14;
const SPARK_RADIUS = 16;
/** The board shows the name of each square while it has this class. */
const NAMED_CLASS = 'board--named';

const squareX = (square: number): number => boardOrigin(square).x;
const squareY = (square: number): number => boardOrigin(square).y;

/** The board on the page, for `squareTarget`. */
let boardOnPage: SVGSVGElement | null = null;

/**
 * The box on the screen of a square, for a coin that leaves it. Null when no
 * board is on the screen. The board is square, so its box scales evenly.
 */
export const squareTarget = (square: number): DOMRect | null => {
  const box = boardOnPage?.getBoundingClientRect();
  if (!box || box.width === 0) return null;
  const scale = box.width / BOARD_PX;
  return new DOMRect(
    box.left + squareX(square) * scale,
    box.top + squareY(square) * scale,
    SQUARE_PX * scale,
    SQUARE_PX * scale,
  );
};

const isDarkSquare = (square: number): boolean => (fileOf(square) + rankOf(square)) % 2 === 0;

/**
 * A piece on the board: its shape in one layer, its badges in the layer over all the shapes.
 * The square and the owner are from the last draw, for the cloud when the piece goes.
 */
type PieceNode = {
  group: SVGGElement;
  badges: BadgeSet;
  shape: string;
  signature: string;
  square: number;
  owner: Owner;
};

/** The place, the turn, and the size of a carried piece. It turns and flattens about the middle of its base. */
const poseTransform = (pose: CarryPose): string =>
  [
    `translate(${pose.x + CARRY_PIVOT_PX.x}px, ${pose.y + CARRY_PIVOT_PX.y}px)`,
    `rotate(${pose.rotate}deg)`,
    `scale(${pose.scaleX}, ${pose.scaleY})`,
    `translate(${-CARRY_PIVOT_PX.x}px, ${-CARRY_PIVOT_PX.y}px)`,
  ].join(' ');

/** What a draw of the pieces changed, for the sound of it. */
type PieceChanges = { left: number; badgeGained: boolean; badgeEnded: boolean };

/** The stage phases with a die or a card over the board. The final card is not one of them. */
const ROLL_PHASES: ReadonlySet<StagePhase> = new Set(['spinning', 'landed', 'leaving']);

const NO_WAITING: ReadonlyMap<number, number> = new Map();
const NO_PITCHES_SHOWN: Pitches = {};

const pieceSignature = (piece: Piece): string =>
  [
    piece.kind,
    piece.owner,
    piece.abilities.join(''),
    piece.lives,
    piece.royal,
    traitSignature(piece),
    roleSignature(piece),
  ].join('|');

/**
 * A string that changes when the shape of the piece does. A new badge does not redraw the
 * shape. The energy slots and the symbol of a caster are part of the shape.
 */
const shapeSignature = (piece: Piece): string =>
  [piece.kind, piece.owner, piece.royal, piece.energy, piece.energyCap, piece.casterId].join('|');

const dot = (square: number, cls: string): SVGCircleElement =>
  svg('circle', {
    class: cls,
    cx: squareX(square) + SQUARE_PX / 2,
    cy: squareY(square) + SQUARE_PX / 2,
    r: DOT_RADIUS,
  });

const tint = (square: number, cls: string): SVGRectElement =>
  svg('rect', {
    class: cls,
    x: squareX(square),
    y: squareY(square),
    width: SQUARE_PX,
    height: SQUARE_PX,
  });

const royalsInDanger = (state: GameState): number[] => {
  if (state.phase === 'over') return [];
  const position = positionOf(state);
  const result: number[] = [];
  state.board.forEach((piece, square) => {
    if (!piece?.royal || piece.owner === 'neutral') return;
    const attacker = piece.owner === 'gold' ? 'violet' : 'gold';
    if (isSquareAttacked(position, square, attacker)) result.push(square);
  });
  return result;
};

/** The marks of a list of moves: a band on an occupied square, a dot on an empty one. */
const moveMarks = (state: GameState, moves: readonly Move[], preview: boolean): SVGElement[] =>
  moves.flatMap((move) =>
    state.board[move.to] !== null
      ? choiceMark(move.to, 'capture', { preview })
      : [dot(move.to, `mark mark--step${preview ? ' mark--preview' : ''}`)],
  );

/**
 * How far the shape of a piece reaches from the middle of its square, in the
 * direction given: to the edge of the box of its silhouette. Up is short for
 * a pawn and long for a king. Zero with no piece.
 */
const shapeReach = (
  piece: Piece | null | undefined,
  direction: { x: number; y: number },
): number => {
  if (!piece) return 0;
  const box = glyphBounds(piece.kind, piece.casterId);
  const middle = GLYPH_BOX / 2;
  const scale = SQUARE_PX / GLYPH_BOX;
  // The distance along the direction to each edge of the box it heads for. The nearer one ends the shape.
  const toSide =
    direction.x > 0
      ? (box.right - middle) / direction.x
      : direction.x < 0
        ? (box.left - middle) / direction.x
        : Number.POSITIVE_INFINITY;
  const toEnd =
    direction.y > 0
      ? (box.bottom - middle) / direction.y
      : direction.y < 0
        ? (box.top - middle) / direction.y
        : Number.POSITIVE_INFINITY;
  return Math.min(toSide, toEnd) * scale;
};

/**
 * The pitches for one move as an arrow under the pieces, from the middle of
 * the square of the piece to where it goes: a band of half dither for each
 * pitch, side by side about the line of the move, and one solid head with a
 * part for each. Until 2026-10-13 a pitch was a band on each of its two
 * squares. A band is also the mark of a royal piece in danger and of a square
 * the piece could go to, so the board did not say which squares were the
 * pitch, or which way it went. Rob: "What's the move being pitched here? It's
 * kind of hard to tell". Then it was a solid arrow over the pieces, see
 * `PITCH_ARROW`. A knight arrow turns 90 degrees at the corner of its path
 * (`movePath`), so it shows the squares the knight crosses. Until 2026-10-15
 * it was one straight line.
 */
const pitchArrow = (board: Board, move: Move, kinds: readonly PitchKind[]): SVGGElement => {
  // The arrow follows the path of the move, so a knight arrow turns at its corner.
  const points = movePath(move.from, move.to).map((square) => ({
    x: squareX(square) + SQUARE_PX / 2,
    y: squareY(square) + SQUARE_PX / 2,
  }));
  const from = points[points.length - 2] ?? points[0];
  const to = points[points.length - 1] ?? from;
  const length = Math.hypot(to.x - from.x, to.y - from.y) || 1;
  const along = { x: (to.x - from.x) / length, y: (to.y - from.y) / length };
  const across = { x: -along.y, y: along.x };

  // The piece that moves covers the tail. The arrow stops clear of a piece it would take,
  // so that piece does not cover the head. On an empty square it goes to the middle.
  const target = board[move.to];
  const tip = target
    ? length - shapeReach(target, { x: -along.x, y: -along.y }) - PITCH_ARROW.clearance
    : length;
  const neck = tip - PITCH_ARROW.headLength;
  const at = (distance: number, off: number) =>
    `${(from.x + along.x * distance + across.x * off).toFixed(1)} ${(from.y + along.y * distance + across.y * off).toFixed(1)}`;
  // A band keeps its lane through each corner: the point where its two offset legs meet.
  const laneCorner = (index: number, off: number): string => {
    const before = points[index - 1] ?? from;
    const corner = points[index] ?? from;
    const after = points[index + 1] ?? to;
    const normal = (a: typeof corner, b: typeof corner) => {
      const span = Math.hypot(b.x - a.x, b.y - a.y) || 1;
      return { x: -(b.y - a.y) / span, y: (b.x - a.x) / span };
    };
    const inbound = normal(before, corner);
    const outbound = normal(corner, after);
    const miter = off / (1 + inbound.x * outbound.x + inbound.y * outbound.y);
    return `${(corner.x + (inbound.x + outbound.x) * miter).toFixed(1)} ${(corner.y + (inbound.y + outbound.y) * miter).toFixed(1)}`;
  };
  const tail = (off: number): string => {
    const start = points[0] ?? from;
    const second = points[1] ?? to;
    const span = Math.hypot(second.x - start.x, second.y - start.y) || 1;
    const x = start.x - ((second.y - start.y) / span) * off;
    const y = start.y + ((second.x - start.x) / span) * off;
    return `${x.toFixed(1)} ${y.toFixed(1)}`;
  };
  const corners = (off: number): string[] =>
    points.slice(1, -1).map((_, index) => laneCorner(index + 1, off));

  const count = kinds.length;
  // A band is a line with the dither as its paint, so a look can give it another paint or width.
  const bands = kinds.map((kind, index) => {
    const lane = (index - (count - 1) / 2) * PITCH_ARROW.laneGap;
    return svg('path', {
      class: `pitch-arrow__band band--${kind}`,
      stroke: `url(#${markDitherId(kind, 'half')})`,
      'stroke-width': PITCH_ARROW.bandWidth,
      d: `M${[tail(lane), ...corners(lane), at(neck, lane)].join(' L')}`,
    });
  });

  // The head is one triangle, cut across into a part for each pitch.
  const headWidth = PITCH_ARROW.headWidth + (count - 1) * PITCH_ARROW.laneGap;
  const edgeAt = (off: number) =>
    neck + PITCH_ARROW.headLength * (1 - Math.abs(off) / (headWidth / 2));
  const heads = kinds.map((kind, index) => {
    const left = -headWidth / 2 + (headWidth / count) * index;
    const right = left + headWidth / count;
    const points = [at(neck, left), at(edgeAt(left), left)];
    if (left < 0 && right > 0) points.push(at(tip, 0));
    points.push(at(edgeAt(right), right), at(neck, right));
    return svg('path', {
      class: `pitch-arrow__head band--${kind}`,
      d: `M${points.join(' L')} Z`,
    });
  });
  return svg('g', { class: 'pitch-arrow' }, [...bands, ...heads]);
};

/** Move targets for a piece the player may move now. */
const destinationMarks = (state: GameState, from: number, preview: boolean): SVGElement[] =>
  moveMarks(state, legalMovesFrom(state, from), preview);

/**
 * Where the piece on the square could move, as a preview: a piece of either
 * side, on either turn, in any mode. Rob, 2026-10-12: "We should show the
 * options for where it could move when it's hovered over". It reads the moves
 * of the piece, not the moves the side may make, so a turn, a roll, or a
 * forced capture does not hide them. A piece that is stopped has none.
 */
const reachMarks = (state: GameState, from: number): SVGElement[] =>
  moveMarks(state, movesForPiece(positionOf(state), from), true);

export const createBoard = (): SVGSVGElement => {
  const root = svg('svg', {
    class: 'board',
    viewBox: `0 0 ${BOARD_PX} ${BOARD_PX}`,
    role: 'grid',
    'aria-label': 'Chess board',
  });
  boardOnPage = root;
  const squaresLayer = svg('g', { class: 'board__squares' });
  const terrain = createTerrainLayer();
  const highlightLayer = svg('g', { class: 'board__highlights' });
  const namesLayer = svg('g', { class: 'board__names', 'aria-hidden': 'true' });
  const piecesLayer = svg('g', { class: 'board__pieces' });
  const badgesLayer = svg('g', { class: 'board__badges' });
  const overlayLayer = svg('g', { class: 'board__overlay' });
  const cloudLayer = svg('g', { class: 'board__clouds' });
  const crossLayer = svg('g', { class: 'board__crosses' });
  const sparkLayer = svg('g', { class: 'board__sparks' });
  const tapLayer = svg('g', { class: 'board__taps' });
  // The marks are under the pieces. Over them, a mark crossed every badge of the piece.
  // The names are over the marks, so a band at the edge of a square does not cover one.
  root.append(
    squaresLayer,
    terrain.layer,
    highlightLayer,
    overlayLayer,
    namesLayer,
    piecesLayer,
    badgesLayer,
    cloudLayer,
    crossLayer,
    sparkLayer,
    tapLayer,
  );

  // The draw replaces the other layers, so the flash has its own.
  const flashTap = (square: number): void => {
    const flash = tint(square, 'tap');
    flash.addEventListener('animationend', () => flash.remove(), { once: true });
    tapLayer.appendChild(flash);
  };

  // A spark runs from the source of a spell to each piece it changed. The
  // circle starts on the source and transitions to the target.
  let sparkedBeat: Beat | null = null;
  const drawSparks = (beat: Beat | null): void => {
    if (beat === sparkedBeat) return;
    sparkedBeat = beat;
    sparkLayer.replaceChildren();
    if (!beat) return;
    for (const spark of beat.sparks) {
      const node = svg('circle', {
        class: `spark spark--${beat.tone ?? 'violet'}`,
        cx: squareX(spark.from) + SQUARE_PX / 2,
        cy: squareY(spark.from) + SQUARE_PX / 2,
        r: SPARK_RADIUS,
      });
      node.style.transition = `transform ${SPARK_TRAVEL_MS}ms var(--ease-out)`;
      sparkLayer.appendChild(node);
      // Force the start position to paint before the move starts.
      void node.getBoundingClientRect();
      const dx = squareX(spark.to) - squareX(spark.from);
      const dy = squareY(spark.to) - squareY(spark.from);
      node.style.transform = `translate(${dx}px, ${dy}px)`;
      node.addEventListener('transitionend', () => node.classList.add('spark--arrived'), {
        once: true,
      });
    }
  };

  const squareNodes: SVGRectElement[] = [];
  const nameNodes: SVGTextElement[] = [];
  for (const square of ALL_SQUARES) {
    const rect = svg('rect', {
      class: `square ${isDarkSquare(square) ? 'square--dark' : 'square--light'}`,
      width: SQUARE_PX,
      height: SQUARE_PX,
    });
    rect.addEventListener('click', () => {
      flashTap(square);
      clickSquare(square);
    });
    // A touch has no hover. A tap picks the square, and the pick is what the inspector reads.
    rect.addEventListener('pointerenter', (event) => {
      if (event.pointerType === 'mouse') hoverSquare(square);
    });
    squaresLayer.appendChild(rect);
    squareNodes.push(rect);
    const name = svg(
      'text',
      { class: `square-name ${isDarkSquare(square) ? 'square-name--dark' : 'square-name--light'}` },
      [squareName(square)],
    );
    namesLayer.appendChild(name);
    nameNodes.push(name);
  }
  /** Puts each square and its name in place. The board turns when this screen plays Moon. */
  const placeSquares = (): void => {
    squareNodes.forEach((rect, square) => {
      rect.setAttribute('x', String(squareX(square)));
      rect.setAttribute('y', String(squareY(square)));
    });
    nameNodes.forEach((name, square) => {
      name.setAttribute('x', String(squareX(square) + SQUARE_NAME_INSET_PX));
      name.setAttribute('y', String(squareY(square) + SQUARE_NAME_INSET_PX));
    });
  };
  placeSquares();
  const drawNames = (): void => {
    root.classList.toggle(NAMED_CLASS, settingsStore.get().squareNames);
  };
  drawNames();
  settingsStore.subscribe(drawNames);
  root.addEventListener('mouseleave', () => hoverSquare(null));

  const nodes = new Map<number, PieceNode>();

  // The piece the AI carries takes its whole transform from the clock, on
  // every frame, and not from its square. The node stays here when the move
  // removes the piece, so it still travels to the square where it ends.
  let carried: { carry: Carry; node: PieceNode } | null = null;
  let carryFrame = 0;

  const poseCarried = (): void => {
    if (!carried) return;
    const transform = poseTransform(poseOfCarry(carried.carry, performance.now()));
    carried.node.group.style.transform = transform;
    carried.node.badges.group.style.transform = transform;
  };

  const carryTick = (): void => {
    carryFrame = 0;
    if (!carried) return;
    poseCarried();
    carryFrame = requestAnimationFrame(carryTick);
  };

  /** Takes up, keeps, or lets go of the carried piece. Call it before the pieces are drawn. */
  const syncCarry = (carry: Carry | null): void => {
    if (carried && carried.carry.pieceId !== carry?.pieceId) {
      carried.node.group.classList.remove('piece--carried');
      carried.node.badges.group.classList.remove('piece--carried');
      carried = null;
    }
    if (!carry) return;
    if (carried) {
      carried.carry = carry;
      return;
    }
    const node = nodes.get(carry.pieceId);
    if (!node) return;
    carried = { carry, node };
    // The last child is drawn on top, so the carried piece passes over the others.
    piecesLayer.appendChild(node.group);
    badgesLayer.appendChild(node.badges.group);
    node.group.classList.add('piece--carried');
    node.badges.group.classList.add('piece--carried');
    if (carryFrame === 0) carryFrame = requestAnimationFrame(carryTick);
  };

  const reducedMotion = window.matchMedia(REDUCED_MOTION_QUERY);

  /**
   * `leaving` is true when a piece that is not in the state left in play. It is false when
   * the board changes to another step or another match, where no piece left at that moment.
   */
  const drawPieces = (
    state: GameState,
    spotlight: Set<number> | null,
    leaving: boolean,
    waiting: ReadonlyMap<number, number>,
    dimOnly: Owner | null,
  ): PieceChanges => {
    // Outside the spotlight a piece is dim. With `dimOnly`, only the pieces of that side are.
    const isDim = (square: number, owner: Owner): boolean =>
      spotlight !== null && !spotlight.has(square) && (dimOnly === null || owner === dimOnly);
    const seen = new Set<number>();
    const changes: PieceChanges = { left: 0, badgeGained: false, badgeEnded: false };
    // The compact board is small, so its badges are one for each corner, large.
    const dense = layoutStore.get() === 'compact';
    state.board.forEach((piece, square) => {
      if (!piece) return;
      seen.add(piece.id);
      // A piece that waits for its step stays as it was drawn: its first square, its shape, its badges.
      const waitingNode = waiting.has(piece.id) ? nodes.get(piece.id) : undefined;
      if (waitingNode) {
        const waitsOn = waitingNode.square;
        const waitingDim = isDim(waitsOn, waitingNode.owner);
        waitingNode.group.classList.toggle('piece--dim', waitingDim);
        waitingNode.badges.group.classList.toggle('piece--dim', waitingDim);
        return;
      }
      const signature = `${pieceSignature(piece)}|${dense}`;
      let node = nodes.get(piece.id);
      const place = `translate(${squareX(square)}px, ${squareY(square)}px)`;
      if (!node) {
        const group = svg('g', { class: 'piece' });
        const badges = createBadgeSet();
        // The badges move with the piece, so their group takes the same class and the same place.
        badges.group.classList.add('piece');
        group.style.transform = place;
        badges.group.style.transform = place;
        node = { group, badges, shape: '', signature: '', square, owner: piece.owner };
        nodes.set(piece.id, node);
        piecesLayer.appendChild(group);
        badgesLayer.appendChild(badges.group);
      }
      node.square = square;
      node.owner = piece.owner;
      const shape = shapeSignature(piece);
      if (node.shape !== shape) {
        node.group.replaceChildren(
          createPieceGlyph(
            piece.kind,
            piece.owner,
            piece.royal,
            { energy: piece.energy, cap: piece.energyCap },
            piece.casterId,
          ),
        );
        node.shape = shape;
      }
      if (node.signature !== signature) {
        // A piece that is new on the board gets its badges with no sound.
        const known = node.signature !== '';
        const change = node.badges.draw(piece, dense);
        if (known && change.gained) changes.badgeGained = true;
        if (known && change.ended) changes.badgeEnded = true;
        node.signature = signature;
      }
      const dim = isDim(square, piece.owner);
      if (node !== carried?.node) {
        node.group.style.transform = place;
        node.badges.group.style.transform = place;
      }
      node.group.classList.toggle('piece--stopped', piece.stoppedTurns > 0);
      node.group.classList.toggle('piece--uncapturable', piece.uncapturableTurns > 0);
      node.group.classList.toggle('piece--dim', dim);
      node.badges.group.classList.toggle('piece--dim', dim);
    });
    for (const [id, node] of nodes) {
      if (seen.has(id)) continue;
      nodes.delete(id);
      changes.left += 1;
      const doomed = [node.group, node.badges.group];
      // A piece the carried piece takes stays until that piece is almost on it.
      const delay = carried ? captureDelayMs(carried.carry, performance.now()) : 0;
      for (const group of doomed) {
        group.style.transitionDelay = `${delay}ms`;
        group.classList.add('piece--gone');
      }
      setTimeout(() => {
        for (const group of doomed) group.remove();
      }, PIECE_LEAVE_MS + delay);
      if (leaving && !reducedMotion.matches) {
        const { square, owner } = node;
        setTimeout(() => cloudLayer.appendChild(createCloud(square, owner)), delay);
      }
    }
    poseCarried();
    return changes;
  };

  /** The sound of what the last draw changed. A piece the AI carries sounds when it is down. */
  const soundChanges = (state: GameState, moved: boolean, changes: PieceChanges): void => {
    if (moved) {
      const delay = carried ? landDelayMs(carried.carry, performance.now()) : 0;
      const mover = state.lastMove ? state.board[state.lastMove.to]?.owner : undefined;
      if (changes.left > 0) playSound('capture', delay);
      else playSound('move', delay, mover === 'violet' ? 1 : 0);
    } else if (changes.left > 0) playSound('cloud');
    if (changes.badgeGained) playSound('badge');
    else if (changes.badgeEnded) playSound('timer');
  };

  const drawHighlights = (
    state: GameState,
    ui: UiState,
    cue: Cue,
    stageShowing: boolean,
    beat: Beat | null,
    step: HistoryStep | null,
    pitches: Pitches,
  ): void => {
    const marks: SVGElement[] = [];
    const overlays: SVGElement[] = [];
    const crosses: SVGElement[] = [];
    // A piece that a pick removes: a dashed band under it and a cross over it.
    const markRemoved = (square: number): void => {
      overlays.push(...choiceMark(square, 'danger', { dashed: true }));
      crosses.push(crossMark(square));
    };

    // The trace is first, so a mark of the present covers it on the same square.
    if (state.lastMove) {
      overlays.push(...traceMark(state.lastMove.from), ...traceMark(state.lastMove.to));
    }

    // An earlier step shows what its spell changed, the way the effect beat did.
    if (step) {
      for (const square of step.squares) overlays.push(...focusMark(square, step.tone ?? 'system'));
    }

    if (beat) {
      const tone = beat.tone ?? 'violet';
      for (const mark of beat.marks) {
        if (mark.kind === 'land' && state.board[mark.square] === null) {
          overlays.push(dot(mark.square, `mark mark--beat-land band--${tone}`));
        } else if (mark.kind === 'land') {
          overlays.push(...choiceMark(mark.square, tone, { dashed: true }));
        } else if (mark.kind === 'remove') {
          markRemoved(mark.square);
        } else if (mark.kind === 'consider') {
          overlays.push(...choiceMark(mark.square, tone));
        } else {
          overlays.push(...focusMark(mark.square, tone));
        }
      }
    }

    for (const square of royalsInDanger(state)) {
      overlays.push(...choiceMark(square, 'danger', { dashed: true }));
    }

    if (!stageShowing && cue.kind === 'aim') {
      const tone = cue.polarity ?? 'ally';
      const hoverClass = `mark mark--aim-hover band--${tone}`;
      const hovered = ui.hovered !== null && cue.hot.includes(ui.hovered) ? ui.hovered : null;
      // A spell that takes a region has a target on every square, so no square has a target mark.
      if (!aimsAtRegion(state)) {
        for (const square of cue.hot) overlays.push(...targetMark(square, tone));
      }
      for (const square of state.pendingTargets) overlays.push(...focusMark(square, 'picked'));
      const region = hovered === null ? null : regionOfPick(state, hovered);
      if (region) {
        // The bands are around the region. The tint is on each square the spell may choose.
        overlays.push(...regionMark(region.squares, tone));
        for (const square of region.open) marks.push(tint(square, hoverClass));
      } else if (hovered !== null) {
        marks.push(tint(hovered, hoverClass));
        for (const square of removedByPick(state, hovered)) markRemoved(square);
      }
    }

    // The pitches of the player: an arrow under the pieces for each move a pitch offers, in
    // the color of the pitch. The careful one is cold and the aggro one is warm. Pitches
    // that are the same move share one arrow. A beat has the floor.
    if (!beat && !stageShowing && cue.mine && cue.kind !== 'aim') {
      const offered: { move: Move; kinds: PitchKind[] }[] = [];
      for (const kind of PITCH_KINDS) {
        const move = pitches[kind];
        if (!move) continue;
        const same = offered.find(
          (other) => other.move.from === move.from && other.move.to === move.to,
        );
        if (same) same.kinds.push(kind);
        else offered.push({ move, kinds: [kind] });
      }
      for (const { move, kinds } of offered) overlays.push(pitchArrow(state.board, move, kinds));
    }

    if (cue.kind === 'move-again' && cue.spotlight) {
      for (const square of cue.spotlight) overlays.push(...choiceMark(square, 'must'));
    }

    // The piece the player picked shows where it goes. With none picked, the piece under
    // the pointer does. A piece the player may move now shows the moves the rules allow.
    // Any other piece shows where it could move. In an aim the squares are targets, so no
    // move shows.
    const read = ui.selected ?? ui.hovered;
    const mayMove =
      read !== null && (cue.kind === 'move' || cue.kind === 'move-again') && cue.hot.includes(read);
    if (read !== null && mayMove) {
      overlays.push(...destinationMarks(state, read, ui.selected === null));
    } else if (read !== null && state.board[read] && cue.kind !== 'aim' && !beat && !stageShowing) {
      overlays.push(...reachMarks(state, read));
    }

    // A piece can be picked to read in any phase, so the pick always shows.
    if (ui.selected !== null) overlays.push(...focusMark(ui.selected, 'select'));

    highlightLayer.replaceChildren(...marks);
    overlayLayer.replaceChildren(...overlays);
    crossLayer.replaceChildren(...crosses);
  };

  terrain.layer.style.pointerEvents = 'none';

  const drawCursors = (state: GameState, ui: UiState, cue: Cue, stageShowing: boolean): void => {
    const hot = new Set<number>();
    if (!stageShowing && cue.mine) {
      for (const square of cue.hot) hot.add(square);
      if (ui.selected !== null) {
        for (const move of legalMovesFrom(state, ui.selected)) hot.add(move.to);
      }
    }
    squareNodes.forEach((rect, square) => {
      rect.classList.toggle('square--hot', hot.has(square));
    });
    root.classList.toggle('board--waiting', !cue.mine && cue.kind !== 'review');
  };

  let boardBeforeRoll: GameState | null = null;
  let drawnLive: GameState | null = null;
  let drawnMove: Move | null = null;
  let drawnDense = false;

  const draw = (): void => {
    const state = viewedGame();
    const ui = uiStore.get();
    // An earlier step is still. The stage and the beats belong to the live game.
    const step = reviewedStep();
    const stageShowing = step === null && stageStore.get().phase !== 'hidden';
    const cue = currentCue();
    const beat = step === null ? beatStore.get() : null;
    let spotlight: Set<number> | null = null;
    // In a move of the player, only the pieces of the player that cannot move are dim.
    let dimOnly: Owner | null = null;
    if (beat?.spotlight) spotlight = new Set(beat.marks.map((mark) => mark.square));
    else if (!stageShowing && cue.mine && cue.spotlight) {
      spotlight = new Set(cue.spotlight);
      if (cue.kind === 'move' || cue.kind === 'move-again') dimOnly = state.turn;
      // A picked piece lights the pieces it can capture.
      if (cue.kind === 'move' && ui.selected !== null) {
        for (const move of legalMovesFrom(state, ui.selected)) spotlight.add(move.to);
      }
    }
    // A spell with no target changes the board on the roll. The board keeps what it showed
    // before the roll until the card leaves, so the change comes after the card and not under it.
    const cardShowing = step === null && ROLL_PHASES.has(stageStore.get().phase);
    if (!cardShowing) boardBeforeRoll = state;
    const shown = cardShowing ? (boardBeforeRoll ?? state) : state;
    terrain.draw(shown.rules);
    syncCarry(step === null ? carryStore.get() : null);
    // A piece left in play when this draw and the last one both show the live match, and
    // the match went on. A step back, a return from one, and a new match are not that.
    const live = step === null ? shown : null;
    const leaving =
      live !== null &&
      drawnLive !== null &&
      live.seed === drawnLive.seed &&
      live.ply >= drawnLive.ply;
    drawnLive = live;
    const moved = leaving && shown.lastMove !== null && shown.lastMove !== drawnMove;
    if (live !== null) drawnMove = shown.lastMove;
    // A change of layout draws every badge again. That is no change of a piece.
    const dense = layoutStore.get() === 'compact';
    const sameLayout = dense === drawnDense;
    drawnDense = dense;
    // An earlier step shows its own board, so no piece waits there.
    const changes = drawPieces(
      shown,
      spotlight,
      leaving,
      step === null ? waitingPiecesStore.get() : NO_WAITING,
      dimOnly,
    );
    if (leaving && sameLayout) soundChanges(shown, moved, changes);
    // The pitches are for the live board.
    const pitches = step === null ? suggestionStore.get() : NO_PITCHES_SHOWN;
    drawHighlights(state, ui, cue, stageShowing, beat, step, pitches);
    drawSparks(beat);
    drawCursors(state, ui, cue, stageShowing);
  };

  draw();
  subscribeView(draw);
  uiStore.subscribe(draw);
  stageStore.subscribe(draw);
  beatStore.subscribe(draw);
  suggestionStore.subscribe(draw);
  carryStore.subscribe(draw);
  waitingPiecesStore.subscribe(draw);
  layoutStore.subscribe(draw);
  localSideStore.subscribe(() => {
    placeSquares();
    draw();
  });
  return root;
};
